import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ChatService, Message } from '../services/chat.service';
import { ChatAuthService } from '../services/chat-auth.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-chat-main',
  templateUrl: './chat-main.component.html',
  styleUrls: ['./chat-main.component.scss']
})
export class ChatMainComponent implements OnInit, OnDestroy {

  @ViewChild('messagesContainer') messagesContainer: ElementRef;

  messages: Message[] = [];
  newMessage: string = '';
  isAITyping: boolean = false;
  isLoading: boolean = false;
  selectedChatId: number | null = null;
  messageCount: number = 0;
  hasReachedLimit: boolean = false;
  conversationLimitReached: boolean = false;
  messageIdCounter = 1;
  errorMessage: string = '';
  isConversationCreated: boolean = false;
  private manualMessageHandling: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private chatService: ChatService,
    private authService: ChatAuthService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit() {
    // Subscribe to route parameter changes to load conversation by ID
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const conversationId = params['id'];
        if (conversationId) {
          // Load conversation from route parameter
          const id = parseInt(conversationId, 10);
          if (!isNaN(id)) {
            // Don't load conversation if we're manually handling messages (just created or sending)
            if (!this.manualMessageHandling) {
              this.loadConversation(id);
            }
          }
        } else {
          // No conversation ID in route, show welcome message
          this.selectedChatId = null;
          this.messages = [this.getWelcomeMessage()];
        }
      });

    // Subscribe to chat service observables
    this.chatService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(messages => {
        // Only show messages if conversation is loaded and not manually handling messages
        if (this.selectedChatId && !this.manualMessageHandling && messages.length > 0) {
          this.messages = messages;
          this.messageIdCounter = messages.length + 1;
        }
        this.scrollToBottom();
      });

    this.chatService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.isLoading = loading;
      });

    this.chatService.messageCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.messageCount = count;
      });

    this.chatService.conversationLimitReached$
      .pipe(takeUntil(this.destroy$))
      .subscribe(hasReached => {
        this.conversationLimitReached = hasReached;
      });
  }

  /**
   * Get welcome message with proper date format
   */
  getWelcomeMessage(): any {
    const now = new Date();
    return {
      id: 0,
      role: 'assistant',
      content: 'Welcome to Analytics Chat! I can help you analyze inspection data, revenue, profitability, and more. Try asking questions like:\n\n• How many inspections did we do in 2024 in Placer County?\n• What was the revenue in Sacramento County?\n• What are the top properties by revenue?\n• Show me profitability by inspectors',
      created_at: now.toISOString()
    };
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle keyboard events in textarea
   */
  handleKeydown(event: KeyboardEvent) {
    // Send message on Enter (without Shift)
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
    // Allow Shift+Enter for new lines (default textarea behavior)
  }

  sendMessage() {
    console.log('sendMessage called. selectedChatId:', this.selectedChatId, 'message:', this.newMessage);

    if (!this.newMessage.trim()) {
      this.errorMessage = 'Please enter a message.';
      return;
    }

    if (this.conversationLimitReached) {
      this.errorMessage = 'This conversation has reached the 100 message limit. Please start a new conversation.';
      return;
    }

    const userInput = this.newMessage;
    this.newMessage = '';
    this.errorMessage = '';

    // Enable manual message handling to prevent service from overwriting
    this.manualMessageHandling = true;

    // Display user message immediately
    const userMessage: Message = {
      id: this.messageIdCounter++,
      role: 'user',
      content: userInput,
      created_at: new Date().toISOString()
    };
    this.messages.push(userMessage);

    this.isAITyping = true;
    this.scrollToBottom();

    // If no conversation exists, create one first
    if (!this.selectedChatId) {
      console.log('No conversation selected. Creating new conversation before sending message.');
      this.createConversationAndSendMessage(userInput);
    } else {
      console.log('Sending message to API:', { conversationId: this.selectedChatId, message: userInput });
      this.sendMessageToAPI(userInput);
    }
  }

  /**
   * Create a new conversation and send the message
   */
  private createConversationAndSendMessage(userInput: string) {
    // Generate conversation title from user's message (first 50 chars)
    const conversationTitle = userInput.length > 50 ? userInput.substring(0, 50) + '...' : userInput;

    console.log('Creating conversation with title:', conversationTitle);

    this.chatService.createConversation(conversationTitle)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Conversation created:', response);
          if (response.success && response.data) {
            this.selectedChatId = response.data.id;
            this.isConversationCreated = true;
            this.messageCount = 0;
            this.conversationLimitReached = false;
            console.log('Conversation created with ID:', this.selectedChatId);

            // Send the message first - it will navigate after completion
            this.sendMessageToAPI(userInput);
          } else {
            this.errorMessage = 'Failed to create conversation: Invalid response format.';
            this.isAITyping = false;
            this.newMessage = userInput; // Restore message on error
            this.manualMessageHandling = false;
          }
        },
        error: (error) => {
          this.errorMessage = 'Failed to create conversation. Please try again.';
          console.error('Error creating conversation:', error);
          this.isAITyping = false;
          this.newMessage = userInput; // Restore message on error
          this.manualMessageHandling = false;
        }
      });
  }

  /**
   * Send message to the API
   */
  private sendMessageToAPI(userInput: string) {
    console.log('Sending message to API:', { conversationId: this.selectedChatId, message: userInput });

    // Don't use takeUntil on message sending to prevent cancellation during navigation
    this.chatService.sendMessage(this.selectedChatId, userInput)
      .subscribe({
        next: (response) => {
          console.log('Message sent successfully:', response);
          this.isAITyping = false;

          // Add AI response message with suggested queries
          if (response.success && response.data && response.data.message) {
            const aiMessage: Message = {
              id: this.messageIdCounter++,
              role: 'assistant',
              content: response.data.message,
              created_at: new Date().toISOString(),
              suggested_queries: response.data.suggested_queries || []
            };
            this.messages.push(aiMessage);
            // Trigger change detection
            this.messages = [...this.messages];
          }

          // Disable manual message handling after response received
          this.manualMessageHandling = false;

          // Navigate to conversation URL if not already there (for new conversations)
          const currentId = this.route.snapshot.params['id'];
          if (!currentId && this.selectedChatId) {
            this.router.navigate(['/milo', this.selectedChatId]);
          }

          this.scrollToBottom();
        },
        error: (error) => {
          console.error('Chat API error:', error);
          this.errorMessage = error.message || 'Sorry, I encountered an error processing your request. Please try again.';
          this.isAITyping = false;
          this.newMessage = userInput; // Restore message on error
          // Disable manual message handling on error
          this.manualMessageHandling = false;
          this.scrollToBottom();
        }
      });
  }

  /**
   * Create a new conversation
   */
  createNewConversation(title?: string) {
    const conversationTitle = title || 'New Chat';
    console.log('Creating new conversation:', conversationTitle);

    this.chatService.createConversation(conversationTitle)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Conversation created:', response);
          if (response.success && response.data) {
            this.selectedChatId = response.data.id;
            this.messages = [this.getWelcomeMessage()];
            this.newMessage = '';
            this.errorMessage = '';
            this.messageCount = 0;
            this.conversationLimitReached = false;
            console.log('Conversation loaded with ID:', this.selectedChatId);

            // Navigate to the new conversation URL
            this.router.navigate(['/milo', this.selectedChatId]);
          } else {
            this.errorMessage = 'Failed to create conversation: Invalid response format.';
          }
        },
        error: (error) => {
          this.errorMessage = 'Failed to create new conversation. Please try again.';
          console.error('Error creating conversation:', error);
        }
      });
  }

  /**
   * Load existing conversation
   */
  loadConversation(conversationId: number) {
    this.selectedChatId = conversationId;
    this.chatService.loadConversation(conversationId);
    this.errorMessage = '';

    // Update URL if not already there (use != for loose comparison to handle string/number)
    const currentId = this.route.snapshot.params['id'];
    if (!currentId || parseInt(currentId, 10) !== conversationId) {
      this.router.navigate(['/milo', conversationId], { replaceUrl: false });
    }
  }

  scrollToBottom() {
    setTimeout(() => {
      if (this.messagesContainer) {
        this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
      }
    }, 0);
  }

  clearChat() {
    if (confirm('Are you sure you want to clear this chat? This action cannot be undone.')) {
      this.messages = [this.getWelcomeMessage()];
      this.newMessage = '';
      this.selectedChatId = null;
      this.errorMessage = '';

      // Navigate back to base chat URL
      this.router.navigate(['/milo']);
    }
  }

  /**
   * Handle suggested query click from chat message
   */
  onSuggestedQueryClick(query: string) {
    this.newMessage = query;
    this.sendMessage();
  }

  /**
   * Logout and redirect to auth page
   */
  logout(): void {
    if (confirm('Are you sure you want to logout?')) {
      this.authService.logout();
      this.router.navigate(['/milo/auth']);
    }
  }

}
